import React from 'react';
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import type { TooltipProps } from 'recharts';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { scaledPx } from 'containers/Intelligence/theme/scale';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import { formatNumber } from 'utils/formatters';

import type { IChartTooltipTheme } from '../tooltipTheme';
import useMeasuredWidth from '../useMeasuredWidth';

export interface ITrendSeries {
  name: string;
  /** A `tokens.color` value — the stroke, and in the area variant the fill, of this trajectory. */
  color: string;
  /** Readings in chronological order; a non-finite reading breaks the line instead of plotting 0. */
  points: number[];
  /** Point-marker colour, where a screen draws the markers in a different hue from the line. */
  markerColor?: string;
}

/** Axis, grid and hover-cursor styling for the axed variant, where a screen draws its own. */
export interface ITrendAxisStyle {
  tickColor?: string;
  /** Tick caption size in drawn px; it follows the responsive scale. */
  tickFontSize?: number;
  gridColor?: string;
  gridDasharray?: string;
  /** Draws the x-axis baseline in this ink; without it the axis has no line. */
  axisLineColor?: string;
  /** Draws 3px x-axis tick marks in this ink; without it there are none. */
  tickMarkColor?: string;
  yAxisWidth?: number;
  cursorColor?: string;
  cursorDasharray?: string;
}

export type TrendChartVariant = 'line' | 'area';

export interface ITrendChartProps {
  series: ITrendSeries[];
  /** Point captions, index-aligned with the readings; they name the hovered point. */
  pointLabels?: string[];
  variant?: TrendChartVariant;
  showLegend?: boolean;
  /**
   * Draws the labelled axes and the horizontal grid the Customer 360 value trend needs. Opt-in and
   * off by default: the 27 Customer Insights cards through this component are sparkline-sized, and
   * axes inside a 56px plot would be unreadable. PLA-8828.
   */
  showAxes?: boolean;
  /** The frame puts the value trend's legend above its plot; every other caller keeps it below. */
  legendPlacement?: 'top' | 'bottom';
  /**
   * Plot box in pixels. `width` is required: P2 draws recharts at an explicit size and never
   * through ResponsiveContainer, which measures 0 in jsdom.
   */
  width: number;
  /**
   * Scales the plot to fill its column rather than drawing at `width`. Opt-in, and it defaults off:
   * Customer Insights renders 27 cards through this component at explicit widths and their pixels
   * must not move. `width` still sets the aspect the box scales by. PLA-8828.
   */
  fluid?: boolean;
  height?: number;
  /** Formats the readings shown on hover. */
  formatValue?: (value: number) => string;
  /**
   * Formats the Y-axis ticks when `showAxes` is on. Defaults to `formatValue`, because a caller with
   * one plain number formatter wants it in both places; pass it separately when the hover readout
   * carries decoration (a unit, a cohort size) that would be wrong on a generated tick.
   */
  formatAxisValue?: (value: number) => string;
  /** Overrides the accessible name; without it the chart is named after the trajectories it draws. */
  ariaLabel?: string;
  /** Line weight in px; defaults to the library's 2.5. */
  strokeWidth?: number;
  /** `linear` joins readings with straight segments instead of the monotone curve. */
  curve?: 'monotone' | 'linear';
  /** Marks every reading with a haloed dot: a 6px disc at 30% under a 3px solid one. */
  pointMarkers?: boolean;
  /** Fixed Y ticks for the axed variant; the domain runs from the first to the last. */
  yTicks?: number[];
  /**
   * X captions spread evenly across the plot, independent of the reading count. PLA-9674 draws 10
   * readings under 7 day labels. Without it each reading is captioned by `pointLabels`.
   */
  xTickLabels?: string[];
  axisStyle?: ITrendAxisStyle;
  /** Gap between the x axis and its captions, so the first caption clears the lowest y caption. */
  xTickMargin?: number;
  /** The hover readout in a screen's own palette. */
  tooltipTheme?: IChartTooltipTheme;
  /** Plot margin in px, overriding the sparkline and axed defaults. */
  margin?: { top: number; right: number; bottom: number; left: number };
  className?: string;
}

/** Figma library "Sparkline trend card" / "Line / area trend with legend" plot 56px tall. */
const DEFAULT_HEIGHT = 56;

/** Figma draws the polylines at 2.49–2.93; the library's own normalisation is one weight. */
const STROKE_WIDTH = 2.5;

const AREA_FILL_OPACITY = 0.16;

const ACTIVE_DOT_RADIUS = 3;

/**
 * A trajectory needs two ADJACENT readings; one reading is a level, which is `StatBox`'s job, and
 * two readings either side of a gap paint no stroke at all (`connectNulls` is off), which would put
 * an invisible chart inside a sized plot box instead of letting the card fall to its state block.
 */
const hasPlottableSegment = (points: (number | null)[]): boolean =>
  points.some(
    (point, index) => index < points.length - 1 && point !== null && points[index + 1] !== null
  );

/** Keeps the stroke inside the box: recharts clips at the plot edge, halving the top reading. */
const PLOT_MARGIN = { top: 3, right: 3, bottom: 3, left: 3 };

/* Axis labels are drawn OUTSIDE the plot, so the sparkline margin clips them to nothing. */
const AXIS_PLOT_MARGIN = { top: 6, right: 6, bottom: 0, left: 0 };

const AXIS_TICK = { fill: tokens.color['text-muted'], fontSize: 11 };

const GRID_COLOR = tokens.color['border-subtle'];

const TOOLTIP_CONTENT_STYLE: React.CSSProperties = {
  padding: `${tokens.space['4']} ${tokens.space['8']}`,
  background: tokens.color['surface-card'],
  border: `1px solid ${tokens.color['border-subtle']}`,
  borderRadius: tokens.radius.sm,
  fontSize: tokens.type['label-size'],
  lineHeight: tokens.type['label-line']
};

const TOOLTIP_ITEM_STYLE: React.CSSProperties = { color: tokens.color['text-primary'] };

const TOOLTIP_LABEL_STYLE: React.CSSProperties = { color: tokens.color['text-muted'] };

const TOOLTIP_CURSOR = { stroke: tokens.color['border-strong'], strokeWidth: 1 };

/* `fluid` MEASURES the column and draws at that width. It used to CSS-stretch the surface to 100%
   and let the viewBox scale it, which drew correctly but broke the hover: recharts resolves the
   hovered index from the cursor's offset against its OWN internal width, so a stretched surface
   reported the point before or after the one under the pointer. `width` stays required as the
   pre-measurement fallback, and every existing caller is byte-identical because none passes
   `fluid`. PLA-8828. */
const TrendWrapper = styled.div<{ $fluid: boolean; $tickFontSize?: number }>`
  display: ${({ $fluid }) => ($fluid ? 'flex' : 'inline-flex')};
  width: ${({ $fluid }) => ($fluid ? '100%' : 'auto')};
  flex-direction: column;

  /* The doubled class outranks the legend's own margin shorthand, which is injected later and would
     otherwise win the cascade on equal specificity and collapse this gap to nothing. */
  && > * + * {
    margin-top: ${tokens.space['8']};
  }

  /* A presentation attribute cannot carry calc(), so a scaled tick size is set from here. */
  ${({ $tickFontSize }) =>
    $tickFontSize === undefined
      ? ''
      : `.recharts-cartesian-axis-tick-value { font-size: ${scaledPx($tickFontSize)}; }`}
`;

const PlotBox = styled.div<{ $fluid: boolean }>`
  display: ${({ $fluid }) => ($fluid ? 'block' : 'inline-flex')};
  width: ${({ $fluid }) => ($fluid ? '100%' : 'auto')};
`;

const LegendList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 calc(-1 * ${tokens.space['4']});
  padding: 0;
  list-style: none;
`;

/* Safari < 14.1 has no flex gap, and the `> * + *` fallback misplaces the first entry of every
   wrapped line — so each entry carries its own trailing margins and the list cancels the last
   row's (conventions/components/cross-browser.md § 5). */
const LegendItem = styled.li`
  display: flex;
  align-items: center;
  margin: 0 ${tokens.space['12']} ${tokens.space['4']} 0;
`;

const LegendDot = styled.span`
  width: 7px;
  height: 7px;
  margin-right: ${tokens.space['4']};
  border-radius: ${tokens.radius.pill};
`;

const LegendLabel = styled.span`
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
`;

const seriesKey = (index: number): string => `series${index}`;

const HALO_RADIUS = 6;
const HALO_OPACITY = 0.3;
const MARKER_RADIUS = 3;

const haloMarker =
  (color: string) =>
  ({ cx, cy, index }: { cx?: number; cy?: number; index?: number }) =>
    cx === undefined || cy === undefined ? (
      <g key={index} />
    ) : (
      <g key={index} data-testid="trend-chart-point-marker">
        <circle cx={cx} cy={cy} r={HALO_RADIUS} fill={color} fillOpacity={HALO_OPACITY} />
        <circle cx={cx} cy={cy} r={MARKER_RADIUS} fill={color} />
      </g>
    );

/** `count` captions at even steps from the first reading's position to the last's. */
const spreadTicks = (count: number, lastIndex: number): number[] =>
  Array.from({ length: count }, (unused, i) => (i * lastIndex) / Math.max(count - 1, 1));

const finiteReading = (value: number): number | null => (Number.isFinite(value) ? value : null);

const TrendChart: React.FC<ITrendChartProps> = ({
  series,
  pointLabels,
  variant = 'line',
  showLegend = false,
  showAxes = false,
  legendPlacement = 'bottom',
  width,
  fluid = false,
  height = DEFAULT_HEIGHT,
  formatValue = formatNumber,
  formatAxisValue,
  ariaLabel,
  strokeWidth = STROKE_WIDTH,
  curve = 'monotone',
  pointMarkers = false,
  yTicks,
  xTickLabels,
  xTickMargin,
  axisStyle,
  margin,
  tooltipTheme,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  // Above the early return: a hook may not be called conditionally. Measured when fluid; the given
  // `width` stands in until the observer reports, and forever in jsdom.
  const plotRef = React.useRef<HTMLDivElement>(null);
  const measured = useMeasuredWidth(plotRef);

  const plotted = (series ?? [])
    .map(trend => ({ ...trend, points: (trend.points ?? []).map(finiteReading) }))
    .filter(trend => hasPlottableSegment(trend.points));

  // A single reading, or none, cannot express a trajectory, and an empty plot box reads as a broken
  // card — the card shows its own state block instead (FR § Data states). A flat line of zeroes is
  // NOT this case: a measured zero is data and still plots.
  if (plotted.length === 0) {
    return null;
  }

  const pointCount = plotted.reduce((longest, trend) => Math.max(longest, trend.points.length), 0);
  const rows = Array.from({ length: pointCount }, (unused, index) =>
    plotted.reduce(
      (row, trend, trendIndex) => ({
        ...row,
        [seriesKey(trendIndex)]: trend.points[index] ?? null
      }),
      (xTickLabels
        ? { label: pointLabels?.[index] ?? '', index }
        : { label: pointLabels?.[index] ?? '' }) as Record<string, string | number | null>
    )
  );
  const spread = showAxes && xTickLabels ? spreadTicks(xTickLabels.length, pointCount - 1) : null;
  const tick = axisStyle?.tickColor ? { ...AXIS_TICK, fill: axisStyle.tickColor } : AXIS_TICK;
  const cursor = axisStyle?.cursorColor
    ? { stroke: axisStyle.cursorColor, strokeWidth: 1, strokeDasharray: axisStyle.cursorDasharray }
    : TOOLTIP_CURSOR;

  // Never an unnamed graphic: without a caller label the plot is named after the trajectories it
  // actually draws, so the default render is still reachable by name.
  const plotLabel =
    ariaLabel ||
    t(`${TCustomerInsights}.Chart.TrendSeries`, {
      series: plotted.map(trend => trend.name).join(', ')
    });

  // recharts' value generic types the formatter's RETURN as well as its argument, so it is declared
  // as the displayed string rather than as the numeric reading it is handed.
  const formatTooltipValue: TooltipProps<string, string>['formatter'] = value =>
    formatValue(Number(value));

  const plotWidth = fluid && measured > 0 ? measured : width;

  const legend = showLegend ? (
    <LegendList data-testid="trend-chart-legend">
      {plotted.map(trend => (
        <LegendItem key={trend.name} data-testid="trend-chart-legend-item">
          <LegendDot
            aria-hidden
            style={{ backgroundColor: trend.color }}
            data-color={trend.color}
            data-testid="trend-chart-legend-dot"
          />
          <LegendLabel data-testid="trend-chart-legend-label">{trend.name}</LegendLabel>
        </LegendItem>
      ))}
    </LegendList>
  ) : null;

  return (
    <TrendWrapper
      $fluid={fluid}
      $tickFontSize={axisStyle?.tickFontSize}
      className={className}
      data-variant={variant}
      data-testid="trend-chart"
    >
      {legendPlacement === 'top' ? legend : null}
      <PlotBox
        ref={plotRef}
        $fluid={fluid}
        role="img"
        aria-label={plotLabel}
        data-testid="trend-chart-plot"
      >
        <AreaChart
          width={plotWidth}
          height={height}
          data={rows}
          margin={margin ?? (showAxes ? AXIS_PLOT_MARGIN : PLOT_MARGIN)}
        >
          {showAxes ? (
            <CartesianGrid
              stroke={axisStyle?.gridColor ?? GRID_COLOR}
              strokeOpacity={axisStyle?.gridColor ? 1 : 0.6}
              strokeDasharray={axisStyle?.gridDasharray}
              vertical={false}
            />
          ) : null}
          {spread && xTickLabels ? (
            <XAxis
              type="number"
              dataKey="index"
              domain={[0, pointCount - 1]}
              ticks={spread}
              tickFormatter={(value: number) => xTickLabels[spread.indexOf(value)] ?? ''}
              tick={tick}
              tickLine={axisStyle?.tickMarkColor ? { stroke: axisStyle.tickMarkColor } : false}
              tickSize={axisStyle?.tickMarkColor ? 3 : undefined}
              axisLine={axisStyle?.axisLineColor ? { stroke: axisStyle.axisLineColor } : false}
              tickMargin={xTickMargin}
              interval={0}
            />
          ) : (
            <XAxis
              dataKey="label"
              hide={!showAxes}
              tick={tick}
              tickLine={axisStyle?.tickMarkColor ? { stroke: axisStyle.tickMarkColor } : false}
              axisLine={axisStyle?.axisLineColor ? { stroke: axisStyle.axisLineColor } : false}
              tickMargin={xTickMargin}
              interval={0}
            />
          )}
          {/* A zero-based domain flattens every trajectory whose readings sit far from zero (a
              sentiment score moving 7.8 → 8.4); the absolute level is printed by the card. The
              axed variant is the exception - the frame's own y-axis opens at 00. */}
          <YAxis
            hide={!showAxes}
            domain={
              showAxes && yTicks?.length
                ? [yTicks[0], yTicks[yTicks.length - 1]]
                : showAxes
                ? [0, 'dataMax']
                : ['dataMin', 'dataMax']
            }
            ticks={showAxes ? yTicks : undefined}
            interval={showAxes && yTicks ? 0 : undefined}
            tick={tick}
            tickLine={false}
            axisLine={false}
            width={showAxes ? axisStyle?.yAxisWidth ?? 38 : undefined}
            tickFormatter={showAxes ? formatAxisValue ?? formatValue : undefined}
          />
          <Tooltip
            cursor={cursor}
            contentStyle={
              tooltipTheme
                ? {
                    ...TOOLTIP_CONTENT_STYLE,
                    background: tooltipTheme.background,
                    border: `1px solid ${tooltipTheme.border}`
                  }
                : TOOLTIP_CONTENT_STYLE
            }
            itemStyle={tooltipTheme ? { color: tooltipTheme.text } : TOOLTIP_ITEM_STYLE}
            labelStyle={tooltipTheme ? { color: tooltipTheme.label } : TOOLTIP_LABEL_STYLE}
            formatter={formatTooltipValue}
            labelFormatter={
              spread ? (value: number) => pointLabels?.[Number(value)] ?? '' : undefined
            }
            isAnimationActive={false}
          />
          {plotted.map((trend, index) => (
            <Area
              key={trend.name}
              type={curve}
              dataKey={seriesKey(index)}
              name={trend.name}
              stroke={trend.color}
              strokeWidth={strokeWidth}
              fill={trend.color}
              fillOpacity={variant === 'area' ? AREA_FILL_OPACITY : 0}
              dot={pointMarkers ? haloMarker(trend.markerColor ?? trend.color) : false}
              activeDot={{ r: ACTIVE_DOT_RADIUS, stroke: 'none', fill: trend.color }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </PlotBox>
      {legendPlacement === 'bottom' ? legend : null}
    </TrendWrapper>
  );
};

export default TrendChart;
